import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, Fade, Menu, MenuItem, Typography, css, popoverClasses, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { SmallAvatar } from '@components/avatar';
import { Button } from '@components/index';
import { RoutingTabs } from '@components/tab';
import { MyProfileModal } from '@pages/customer/employee/gnb/myProfileModal';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import TailImg from '@images/tail.png';
import { ProfileIcon } from '@icons/ProfileIcon';
import { PersonIcon, LogoutIcon, StoreFillIcon, StoreIcon, TriangleIcon, GlobeIcon, CheckIcon } from '@icons/index';
import { useLogout } from '@queryHooks/index';
import { useGetTenant } from '@queryHooks/useTenant';
import { transientOptions } from '@utils/CommonUtil';
import { getDomainFromBaseUrl } from '@utils/urlUtils';
import i18n from '@locales/i18n';

const { ENABLED_PRIVACY_POLICY } = getEnvConfig();

const profileStyles = {
  elevation: 0,
  sx: {
    overflow: 'visible',
    boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.06);',
    borderRadius: '3px',
    mt: '18px',
    border: '1px solid #e1e4e8',
    '& .MuiList-root': {
      padding: '6px',
      border: 'none',
      boxShadow: 'none',
      borderRadius: '3px',
    },
    '& .MuiAvatar-root': {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: -6,
      right: 40,
      width: 14,
      height: 7,
      bgcolor: 'background.paper',
      background: `url(${TailImg}) 0 0 no-repeat`,
      backgroundSize: '14px 7px',
      webkitBackgroundSize: '14px 7px',
      zIndex: 0,
    },
  },
};

const ManagerGnb = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = location.pathname.split('/')[2];
  const [isLangOpen, setIsLangOpen] = useState<boolean>(false);
  const enabledPrivacyPolicy = ENABLED_PRIVACY_POLICY === 'true';

  const {
    authStore,
    uiStore: { profileModalStore: modal },
  } = useStore();
  const { data: tenant } = useGetTenant(authStore.curTenant.id);

  // 구독 관리자 전용 여부 (Manager.tsx에서 isInitialized 가드 담당)
  const isSubscriptionOwnerOnly = authStore.isSubscriptionOwnerOnly;

  // 구독 관리자 전용인 경우 구독 메뉴만 표시
  const allMenus = useMemo(
    () => [
      { name: t('GNB.menu.home'), path: `/manager/${tenantId}/home` },
      { name: t('GNB.menu.subscriptions'), path: `/manager/${tenantId}/subscriptions` },
      { name: t('GNB.menu.costs'), path: `/manager/${tenantId}/billings` },
      { name: t('GNB.menu.workflows'), path: `/manager/${tenantId}/workflows` },
      { name: t('GNB.menu.settings'), path: `/manager/${tenantId}/usergroups` },
    ],
    [t, tenantId],
  );

  const menuTabList = useMemo(
    () => (isSubscriptionOwnerOnly ? allMenus.filter(menu => menu.path.includes('/subscriptions')) : allMenus),
    [allMenus, isSubscriptionOwnerOnly],
  );
  const logoUrl = tenant?.logoImageUrl ?? '';
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuOwnerRef = useRef<HTMLDivElement>(null);

  const { logout } = useLogout();

  const handleClickProfileMenu = () => {
    setIsProfileMenuOpen(true);
  };
  const handleCloseProfileMenu = () => {
    setIsProfileMenuOpen(false);
    setIsLangOpen(false);
  };

  const handleChangeLang = (lang: string) => {
    i18n.changeLanguage(lang);
    dayjs.locale(lang);
    localStorage.setItem('SMP_lang', lang);

    const isSecure = window.location.protocol === 'https:';
    const secureAttr = isSecure ? ' secure;' : '';

    // COMPANY_ID에 따른 도메인 설정
    const domainAttr = getDomainFromBaseUrl();
    const cookieString = `SMP_lang=${lang}; path=/;${secureAttr} ${domainAttr}`;
    document.cookie = cookieString;

    setIsLangOpen(false);
  };
  const isStore = location.pathname.split('/')[3] === 'store';

  const handleLogout = () => {
    handleCloseProfileMenu();
    logout();
    navigate(`/manager/${tenantId}/login`);
  };

  return (
    <Container>
      {matches ? null : (
        <>
          <Logo>
            <HomeLink to={`/manager/${tenantId}`}>
              {logoUrl ? (
                <CustomLogoWrapper>
                  <img alt="logo" src={logoUrl} />
                </CustomLogoWrapper>
              ) : (
                <Title>{tenant?.name ?? ''}</Title>
              )}
            </HomeLink>
            <ManagerLogo>
              <Typography component="span" variant="caption5" color={theme.colors['text-white']}>
                {t('GNB_Menu_06')}
              </Typography>
            </ManagerLogo>
          </Logo>

          {/* routingTabs 개선 후 적용 예정 */}
          <MenusWrapper>
            <RoutingTabs isAdmin variant="line" tabList={menuTabList} />
          </MenusWrapper>
          <RightElementWrapper>
            {/* 전체검색 임시 삭제 */}
            {/* <Button variant="text" size="small" onClick={() => navigate('/manager/search')}>
              <SearchIcon width={28} height={28} color={theme.colors['ic-gray-lighter']} />
            </Button> */}
            {enabledPrivacyPolicy && (
              <PrivacyPolicyLink onClick={() => navigate('/privacy-policy')}>{t('Sign_In_08')}</PrivacyPolicyLink>
            )}
            {/* 구독 관리자 전용인 경우 스토어 버튼 숨김 */}
            {!isSubscriptionOwnerOnly && (
              <IconButton
                variant="text"
                size="large"
                onClick={() => navigate(`/manager/${tenantId}/store`)}
                $isStore={isStore}
              >
                {!isStore ? (
                  <StoreContainer>
                    <StoreIcon width={24} height={24} bgColor={theme.colors['ic-gray-lighter']} />
                    <StoreContainerText>{t('GNB_Menu_07')}</StoreContainerText>
                  </StoreContainer>
                ) : (
                  <StoreContainer>
                    <StoreFillIcon width={24} height={24} bgColor={theme.colors['ic-white']} />
                    <StoreContainerText>{t('GNB_Menu_07')}</StoreContainerText>
                  </StoreContainer>
                )}
              </IconButton>
            )}
            <Profile
              ref={profileMenuOwnerRef}
              onMouseEnter={handleClickProfileMenu}
              onMouseLeave={handleCloseProfileMenu}
              className={authStore.isItAdmin ? 'admin-profile' : ''}
            >
              <SmallAvatar>
                <ProfileIcon width={28} height={28} bgColor={theme.colors['bg-primary']} />
              </SmallAvatar>
            </Profile>
            <Menu
              anchorEl={profileMenuOwnerRef.current}
              open={isProfileMenuOpen}
              onClose={handleCloseProfileMenu}
              MenuListProps={{
                onMouseEnter: handleClickProfileMenu,
                onMouseLeave: handleCloseProfileMenu,
                sx: { pointerEvents: 'auto' },
              }}
              TransitionComponent={Fade}
              transformOrigin={{ horizontal: 'center', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              PaperProps={profileStyles}
              sx={{
                [`&.${popoverClasses.root}`]: {
                  pointerEvents: 'none',
                  marginTop: '-1px',
                },
              }}
            >
              <StyledMenuItem
                value="profile"
                onClick={() => {
                  modal.open();
                  handleCloseProfileMenu();
                }}
              >
                <ProfileMenuWrapper>
                  <PersonIcon width={18} height={18} />
                  <Typography noWrap component="span" variant="body1" color={theme.colors['text-gray-main']}>
                    {t('GNB_Profile_Menu_01')}
                  </Typography>
                </ProfileMenuWrapper>
              </StyledMenuItem>
              <StyledMenuItem value="language">
                <LanguageWrapper onClick={() => setIsLangOpen(prev => !prev)}>
                  <GlobeIcon width={18} height={18} />
                  <Typography
                    noWrap
                    component="span"
                    variant="body1"
                    color={theme.colors['text-gray-main']}
                    sx={{ marginRight: 'auto' }}
                  >
                    {t('GNB_Profile_Menu_02')}
                  </Typography>
                  <TriangleIcon rotateNum={90} width={16} height={16} color={theme.colors['ic-gray-dark']} />
                  {isLangOpen && (
                    <Languages>
                      <Language onClick={() => handleChangeLang('ko')}>
                        <span className={i18n.language === 'ko' ? 'ko' : undefined}>{t('Language.Korean')}</span>
                        {i18n.language === 'ko' && (
                          <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />
                        )}
                      </Language>
                      <Language onClick={() => handleChangeLang('en')}>
                        <span className={i18n.language === 'en' ? 'en' : undefined}>{t('Language.English')}</span>
                        {i18n.language === 'en' && (
                          <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />
                        )}
                      </Language>
                    </Languages>
                  )}
                </LanguageWrapper>
              </StyledMenuItem>
              <StyledMenuItem value="logout" onClick={handleLogout}>
                <ProfileMenuWrapper>
                  <LogoutIcon width={18} height={18} />
                  <Typography noWrap component="span" variant="body1" color={theme.colors['text-gray-main']}>
                    {t('GNB_Profile_Menu_03')}
                  </Typography>
                </ProfileMenuWrapper>
              </StyledMenuItem>
            </Menu>
          </RightElementWrapper>
        </>
      )}
      {modal.visible && <MyProfileModal />}
    </Container>
  );
});

const Container = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  height: var(--manager-gnb-height);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
  padding: 0px 50px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-main']};
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.09);
  z-index: 1001;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    padding: 0 20px;
  }
`;

const RightElementWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  height: inherit;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
  z-index: 1001;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    & .MuiButtonBase-root.MuiIconButton-root {
      margin-right: 0;
      &:first-of-type {
        margin-right: 12px;
      }
    }
  }
  &:hover > span {
    background-color: ${({ theme }) => theme.colors['bg-gray-main']};
  }
`;

const CustomLogoWrapper = styled.div`
  position: relative;
  height: 25px;

  & > img {
    width: auto;
    max-width: 300px;
    height: 100%;
    object-fit: contain;
  }
`;

const Logo = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  z-index: 1001;
`;

const Title = styled.span`
  ${fonts.Display3};
  color: ${({ theme: { colors } }) => colors['text-white']};
`;

const HomeLink = styled(Link)`
  text-decoration: none;
`;

const ManagerLogo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 2px 8.5px;
  background: ${({ theme: { colors } }) => colors['bg-gradient']};
  border-radius: 2px;
`;

const Profile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;

  & > span {
    ${fonts.Button4}
    color: ${({ theme: { colors } }) => colors['text-white']}
  }
`;
const MenusWrapper = styled(Box)`
  max-width: 1440px;
  display: flex;
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 0);
  justify-content: center;
  gap: 32px;
  align-items: center;
  & .en-tab-item {
    height: 100%;
    min-width: auto;
    padding: 0 46px;
  }
`;

const IconButton = styled(Button, transientOptions)<{ $isStore: boolean }>`
  width: 76px;
  height: 24px;
  &:hover {
    svg .line {
      fill: ${({ theme }) => theme.colors['ic-white']};
    }
  }
  & span {
    position: relative;
  }

  & span::after {
    ${({ $isStore, theme: { colors } }) =>
      $isStore &&
      css`
        content: '';
        display: block;
        position: absolute;
        left: 0;
        bottom: -17px;
        width: 100%;
        height: 3px;
        background-color: ${colors['bg-white']};
      `}
  }
`;

const StyledMenuItem = styled(MenuItem)`
  width: 198px;
  padding: 8px 4px 8px 11px;
  border-radius: 2px;
  overflow: visible;
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;
const ProfileMenuWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: center;
`;

const StoreContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  /* margin-right: 25px; */
  height: 24px;
`;

const StoreContainerText = styled.span`
  color: ${({ theme }) => theme.colors['text-white']};
  text-align: center;
  ${fonts.Button2};
`;

const LanguageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  gap: 6px;
`;

const Languages = styled.ul`
  position: absolute;
  right: 200px;
  top: -7px;
  width: 180px;
  height: fit-content;
  padding: 6px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 3px;
  background-color: ${props => props.theme.colors['bg-white']};
  z-index: 1001;
`;

const Language = styled.li`
  display: flex;
  flex-direction: row;
  padding: 10px 0px 10px 11px;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  color: ${props => props.theme.colors['text-primary-light']};
  ${fonts.Body2};
  &:hover {
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
  & > span {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
    &.ko,
    &.en {
      color: ${({ theme }) => theme.colors['text-primary-light']};
    }
  }
`;

const PrivacyPolicyLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 24px;
  ${fonts.Button2};
  white-space: nowrap;

  color: ${({ theme }) => theme.colors['text-white']};
`;

export default ManagerGnb;
